'use client';

import { useCallback } from 'react';

import { ANALYTICS_EVENT_TYPE } from '@workspace/lib/constants/analytics';
import { useRestApiAnalyticsData } from '@workspace/lib/hooks/analytics/use-rest-api-analytics-data';
import {
  SESSION_STORE_KEYS,
  useSessionStore,
  type SessionData,
} from '@workspace/lib/src/hooks/store/use-session-store';
import { sendRestAnalyticsEvent } from '@workspace/lib/src/utils/analytics-rest';
import type {
  AnalyticsEventOptions,
  EndpointSpecificAnalytics,
} from '@workspace/lib/types/analytics';

export function useAnalyticsEvents() {
  const { composeAnalytics } = useRestApiAnalyticsData();
  const { getFromSessionStore } = useSessionStore();

  const sendEvent = useCallback(
    async (
      eventData: EndpointSpecificAnalytics,
      options?: Omit<AnalyticsEventOptions, 'messageId' | 'userId'>,
    ) => {
      const messageId = getFromSessionStore(SESSION_STORE_KEYS.messageId);
      const userId = getFromSessionStore(SESSION_STORE_KEYS.userId);
      if (!messageId || !userId) return;

      const sessionData = getFromSessionStore(
        SESSION_STORE_KEYS.sessionData,
      ) as SessionData;
      const applicationId = sessionData?.applicationId ?? '';

      await sendRestAnalyticsEvent(composeAnalytics(eventData), {
        messageId: messageId as string,
        userId,
        ...(applicationId && { applicationId }),
        ...options,
      });
    },
    [composeAnalytics, getFromSessionStore],
  );

  const trackPageView = useCallback(
    async (page: string) => {
      await sendEvent({
        event: {
          eventType: ANALYTICS_EVENT_TYPE.PAGE_VIEW,
          eventName: page,
        },
      });
    },
    [sendEvent],
  );

  const trackFormSubmit = useCallback(
    async (formName: string) => {
      await sendEvent({
        event: {
          eventType: ANALYTICS_EVENT_TYPE.SUBMIT,
          eventName: formName,
        },
      });
    },
    [sendEvent],
  );

  const trackButtonClick = useCallback(
    async (buttonName: string) => {
      await sendEvent({
        event: {
          eventType: ANALYTICS_EVENT_TYPE.CTA_CLICK,
          eventName: buttonName,
        },
      });
    },
    [sendEvent],
  );

  const trackError = useCallback(
    async (error: Error, context?: string) => {
      await sendEvent({
        event: {
          eventType: ANALYTICS_EVENT_TYPE.PAGE_VIEW,
          eventName: '/technical-error',
          // TODO: clarify if it possible to send additional data
          // eventData: { error },
        },
      });
    },
    [sendEvent],
  );

  // @TODO: Implement failed REST API events retrying
  // const retryFailedEvents = useCallback(async (maxRetries?: number) => {
  // }, []);

  return {
    // Custom event
    sendEvent,

    // Specific events
    trackPageView,
    trackFormSubmit,
    trackButtonClick,
    trackError,
  };
}
